import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getBackendRoot } from "@/lib/api";
import { proxied } from "@/lib/imageProxy";
import {
  Megaphone, Loader2, Check, Bot, Send, Image as ImageIcon, Upload, Settings2, RefreshCw, Sparkles,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function absUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const root = getBackendRoot();
  const base = root || window.location.origin;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

export function AdminMetaAds() {
  const [tab, setTab] = useState("listing");
  const [drafts, setDrafts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [uploads, setUploads] = useState([]);

  const [settForm, setSettForm] = useState({
    ad_account_id: "", page_id: "", access_token: "", default_daily_budget_huf: 3500,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dListing, dBrand, tpl, sett] = await Promise.all([
        api.get("/admin/meta-ads/drafts", { params: { kind: "listing" } }),
        api.get("/admin/meta-ads/drafts", { params: { kind: "brand" } }),
        api.get("/admin/meta-ads/templates"),
        api.get("/admin/meta-ads/settings"),
      ]);
      setDrafts([...(dListing.data.drafts || []), ...(dBrand.data.drafts || [])]);
      setTemplates(tpl.data.templates || []);
      setSettings(sett.data);
      setSettForm((f) => ({
        ...f,
        ad_account_id: sett.data.ad_account_id || "",
        page_id: sett.data.page_id || "",
        default_daily_budget_huf: sett.data.default_daily_budget_huf || 3500,
      }));
    } catch (_) {
      toast.error("Meta AI adatok betöltése sikertelen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (draftId) => {
    setApproving(draftId);
    try {
      const r = await api.post(`/admin/meta-ads/drafts/${draftId}/approve`);
      const pub = r.data.publish || {};
      if (pub.status === "pending_connection") {
        toast.success("Jóváhagyva — Meta bekötés után mehet élesbe.");
      } else {
        toast.success("Jóváhagyva / Meta felé elküldve.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sikertelen");
    } finally {
      setApproving(null);
    }
  };

  const saveSettings = async () => {
    try {
      await api.patch("/admin/meta-ads/settings", settForm);
      toast.success("Meta beállítások mentve");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mentés sikertelen");
    }
  };

  const seedTemplates = async () => {
    try {
      await api.post("/admin/meta-ads/templates/seed");
      toast.success("Sablon kreatívok legenerálva (logóval)");
      load();
    } catch (e) {
      toast.error("Sablon generálás sikertelen");
    }
  };

  const onUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      const r = await api.post("/admin/meta-ads/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = r.data.files || [];
      setUploads((u) => [...u, ...next]);
      toast.success(`${next.length} fájl feltöltve — használd a chatben`);
    } catch (_) {
      toast.error("Feltöltés sikertelen");
    }
    e.target.value = "";
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatLoading(true);
    const userTurn = { role: "user", content: text };
    setChatHistory((h) => [...h, userTurn]);
    setChatInput("");
    try {
      const r = await api.post("/admin/meta-ads/chat", {
        message: text,
        history: chatHistory.slice(-16),
        asset_urls: uploads.map((u) => u.absolute_url || absUrl(u.url)),
      });
      setChatHistory((h) => [...h, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chat hiba");
    } finally {
      setChatLoading(false);
    }
  };

  const createBrandDraftFromTemplate = async (tpl) => {
    try {
      await api.post("/admin/meta-ads/brand/draft", {
        media_url: tpl.url,
        media_type: "image",
        title: `Sablon ${tpl.template_id}`,
        copy: { primary_text: "AutoPark — prémium használtautók Magyarországon.", headline: "AutoPark" },
      });
      toast.success("Brand draft létrehozva");
      load();
      setTab("listing");
    } catch (_) {
      toast.error("Draft létrehozás sikertelen");
    }
  };

  const listingDrafts = drafts.filter((d) => d.kind === "listing");
  const brandDrafts = drafts.filter((d) => d.kind === "brand");

  if (loading && !drafts.length) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-7 h-7 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-meta-ads">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-light flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-electric" strokeWidth={1.5} />
            Meta hirdetés AI
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Eladói Meta kérés → automatikus draft (logó a képeken). Brand kampány: chat + sablonok + saját fotó/videó.
            Meta fiók később beköthető — addig is jóváhagyod a draftot itt.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full glass text-sm text-slate-300 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" /> Frissítés
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/[0.04] border border-white/10 p-1 rounded-xl">
          <TabsTrigger value="listing" data-testid="meta-tab-listing" className="rounded-lg data-[state=active]:bg-electric data-[state=active]:text-white">
            Eladói hirdetések ({listingDrafts.length})
          </TabsTrigger>
          <TabsTrigger value="brand" data-testid="meta-tab-brand" className="rounded-lg data-[state=active]:bg-electric data-[state=active]:text-white">
            AutoPark brand
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="meta-tab-settings" className="rounded-lg data-[state=active]:bg-electric data-[state=active]:text-white">
            Meta bekötés
          </TabsTrigger>
        </TabsList>

        <TabsContent value="listing" className="mt-6 space-y-4">
          {listingDrafts.length === 0 ? (
            <p className="text-slate-400 text-sm">Még nincs Meta draft. Eladói „Meta hirdetés” kiegészítő után automatikusan készül.</p>
          ) : (
            listingDrafts.map((d) => (
              <DraftCard key={d.id} draft={d} approving={approving} onApprove={approve} />
            ))
          )}
          {brandDrafts.length > 0 && (
            <div className="pt-4 border-t border-white/10">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Brand draftok</h3>
              {brandDrafts.map((d) => (
                <DraftCard key={d.id} draft={d} approving={approving} onApprove={approve} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="brand" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-electric" /> Sablon kreatívok
              </h3>
              <button type="button" onClick={seedTemplates} className="text-xs text-electric hover:underline">
                Generálás / frissítés
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {templates.map((t) => (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => createBrandDraftFromTemplate(t)}
                  className="rounded-xl overflow-hidden border border-white/10 hover:border-electric/40 transition-colors text-left"
                >
                  <img src={proxied(absUrl(t.url))} alt={t.template_id} className="w-full aspect-square object-cover" />
                  <span className="block text-[10px] p-2 text-slate-400 uppercase tracking-wider">{t.template_id}</span>
                </button>
              ))}
            </div>
            {templates.length === 0 && (
              <p className="text-xs text-slate-500">Kattints a „Generálás” gombra — 3 sablon logóval.</p>
            )}
          </div>

          <div className="glass rounded-2xl p-5 flex flex-col min-h-[420px]" data-testid="meta-brand-chat">
            <h3 className="font-medium flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-electric" /> Chat — AutoPark Meta kampány
            </h3>
            <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer mb-3">
              <Upload className="w-3.5 h-3.5" />
              Fotó / videó csatolása
              <input type="file" accept="image/*,video/*" className="sr-only" onChange={onUpload} data-testid="meta-brand-upload" />
            </label>
            {uploads.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {uploads.map((u, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-slate-300">
                    {u.media_type === "video" ? "Videó" : "Kép"} #{i + 1}
                  </span>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1 max-h-64">
              {chatHistory.length === 0 && (
                <p className="text-xs text-slate-500">Pl.: „Készíts tavaszi kampányt 50e Ft budgettel, cél: forgalom a főoldalra.”</p>
              )}
              {chatHistory.map((m, i) => (
                <div key={i} className={`text-sm rounded-xl px-3 py-2 ${m.role === "user" ? "bg-electric/20 ml-8" : "bg-white/[0.04] mr-8 text-slate-300"}`}>
                  {m.content}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendChat()}
                placeholder="Üzenet…"
                className="flex-1 rounded-full bg-black/30 border border-white/10 px-4 py-2 text-sm"
                data-testid="meta-brand-chat-input"
              />
              <button
                type="button"
                disabled={chatLoading}
                onClick={sendChat}
                className="w-10 h-10 rounded-full bg-electric flex items-center justify-center disabled:opacity-50"
                data-testid="meta-brand-chat-send"
              >
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-6 max-w-lg space-y-4 glass rounded-2xl p-6">
          <h3 className="font-medium flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-electric" /> Meta Marketing API (később)
          </h3>
          <p className="text-xs text-slate-400">
            {settings?.connected
              ? "Bekötve — jóváhagyáskor megpróbáljuk a Meta felé küldeni."
              : "Még nincs fiók? A draftok és logós képek így is működnek; token után éles publish."}
          </p>
          <label className="block text-xs text-slate-500">Ad account ID (act_…)</label>
          <input
            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm"
            value={settForm.ad_account_id}
            onChange={(e) => setSettForm({ ...settForm, ad_account_id: e.target.value })}
            data-testid="meta-settings-ad-account"
          />
          <label className="block text-xs text-slate-500">Facebook Page ID</label>
          <input
            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm"
            value={settForm.page_id}
            onChange={(e) => setSettForm({ ...settForm, page_id: e.target.value })}
            data-testid="meta-settings-page"
          />
          <label className="block text-xs text-slate-500">Access token (System User)</label>
          <input
            type="password"
            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm"
            placeholder={settings?.access_token_set ? "•••• mentve — új token felülírja" : ""}
            value={settForm.access_token}
            onChange={(e) => setSettForm({ ...settForm, access_token: e.target.value })}
            data-testid="meta-settings-token"
          />
          <button
            type="button"
            onClick={saveSettings}
            className="px-5 py-2.5 rounded-full bg-electric text-white text-sm font-medium"
            data-testid="meta-settings-save"
          >
            Mentés
          </button>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DraftCard({ draft, approving, onApprove }) {
  const c = draft.copy || {};
  const b = draft.budget || {};
  const canApprove = !["approved_awaiting_meta", "approved_local", "live_pending_meta"].includes(draft.status);

  return (
    <div className="glass rounded-2xl p-5" data-testid={`meta-draft-${draft.id}`}>
      <div className="flex flex-wrap justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{draft.kind} · {draft.status}</div>
          <div className="font-medium mt-1">
            {draft.car ? `${draft.car.make} ${draft.car.model} (${draft.car.year})` : draft.title || "Meta draft"}
          </div>
          {draft.schedule?.until && (
            <div className="text-xs text-slate-400 mt-1">Határidő: {new Date(draft.schedule.until).toLocaleDateString("hu-HU")}</div>
          )}
        </div>
        <div className="text-right text-sm">
          <div className="text-slate-400">Budget</div>
          <div>{b.total_huf ? `${b.total_huf.toLocaleString("hu-HU")} Ft összesen` : "—"}</div>
          <div className="text-xs text-slate-500">~{b.daily_huf?.toLocaleString("hu-HU")} Ft / nap</div>
        </div>
      </div>
      <p className="text-sm text-slate-300 mb-2">{c.primary_text}</p>
      <p className="text-xs text-slate-500 mb-4">{c.headline} · {c.link_url}</p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {(draft.creatives || []).map((cr, i) => (
          <div key={i} className="shrink-0 w-32">
            <img
              src={proxied(absUrl(cr.url))}
              alt={cr.format || "creative"}
              className="w-32 h-32 object-cover rounded-lg border border-white/10"
            />
            <span className="text-[9px] text-slate-500">{cr.format}</span>
          </div>
        ))}
      </div>
      {canApprove && (
        <button
          type="button"
          disabled={approving === draft.id}
          onClick={() => onApprove(draft.id)}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-sm disabled:opacity-50"
          data-testid={`meta-draft-approve-${draft.id}`}
        >
          {approving === draft.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Jóváhagyás {draft.kind === "listing" ? "(Meta felé)" : ""}
        </button>
      )}
    </div>
  );
}
